<script setup>
/**
 * Bookings & Trips — the customer's travel across every conversation they have ever had.
 *
 * The conversation sidebar answers "what is this thread about" and stays narrow; this
 * answers "what has this customer ever had", which is why the whole history lives here and
 * not there.
 *
 * Active first, past behind "View all": an agency opening a customer wants the journey
 * that has a deadline on it, not the one they flew last year.
 *
 * NO PAYMENTS. Nothing links a Chatwoot invoice to a Travelport booking, so a row here
 * claiming a trip was paid for would be inventing a relationship. Invoices are contact
 * activity and stay contact-scoped. Fares, ticketing and documents are travel events and
 * do belong here.
 */
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { calendarDate } from 'shared/helpers/timeHelper';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import TravelTripsAPI from 'dashboard/api/travel/trips';
import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TripDetailsDialog from 'dashboard/components-next/Travel/TripDetailsDialog.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  selectedContact: { type: Object, required: true },
});

const { t } = useI18n();
const { isCloudFeatureEnabled } = useAccount();

const trips = ref([]);
const isLoading = ref(false);
const showAll = ref(false);
const openTripId = ref(null);

const isEnabled = computed(() => isCloudFeatureEnabled(FEATURE_FLAGS.TRAVEL));

// `closed` is the only status that means the agency is done with the journey. Everything
// else is still live in some sense, including a ticketed trip the customer has not flown.
const active = computed(() =>
  trips.value.filter(trip => trip.status !== 'closed')
);
const past = computed(() =>
  trips.value.filter(trip => trip.status === 'closed')
);

const visible = computed(() =>
  showAll.value ? [...active.value, ...past.value] : active.value
);

const fetchTrips = async () => {
  if (!isEnabled.value || !props.selectedContact?.id) return;

  isLoading.value = true;
  try {
    const { data } = await TravelTripsAPI.get({
      contactId: props.selectedContact.id,
    });
    trips.value = data.trips || [];
  } catch (error) {
    // A travel outage must not break the customer record an agent came here to read.
    trips.value = [];
  } finally {
    isLoading.value = false;
  }
};

const route = trip => {
  const { origin, destination } = trip.requirements || {};
  return origin && destination
    ? `${origin} → ${destination}`
    : t('CONTACTS_LAYOUT.DETAILS.TRIPS.NO_ROUTE');
};

const dates = trip => {
  const { departure_date: departure, return_date: back } =
    trip.requirements || {};
  return [departure, back]
    .filter(Boolean)
    .map(date => calendarDate(date))
    .join(' — ');
};

// A refresh or an acknowledgement made inside the dialog has to land on the row behind it.
const replaceTrip = updated => {
  trips.value = trips.value.map(trip =>
    trip.id === updated.id ? { ...trip, ...updated } : trip
  );
};

// The nearest thing to a deadline this trip has. The server decides what a hold state
// means and writes the sentence, so this only picks which one to show.
const deadline = trip => {
  if (trip.schedule_changed)
    return {
      text: t('CONTACTS_LAYOUT.DETAILS.TRIPS.SCHEDULE_CHANGED'),
      urgent: true,
    };
  if (!trip.hold?.label) return null;

  return {
    text: trip.hold.label,
    urgent: ['expiring', 'expired'].includes(trip.hold.state),
  };
};

watch(() => props.selectedContact?.id, fetchTrips, { immediate: true });
</script>

<template>
  <CardLayout v-if="isEnabled">
    <div class="flex items-center justify-between gap-3">
      <h4 class="text-sm font-medium text-n-slate-12">
        {{ t('CONTACTS_LAYOUT.DETAILS.TRIPS.TITLE') }}
      </h4>
      <Button
        v-if="past.length && !showAll"
        link
        slate
        sm
        :label="
          t('CONTACTS_LAYOUT.DETAILS.TRIPS.VIEW_ALL', { count: past.length })
        "
        @click="showAll = true"
      />
    </div>

    <Spinner v-if="isLoading" class="mx-auto mt-4" />
    <p v-else-if="!visible.length" class="mt-3 text-sm text-n-slate-11">
      {{ t('CONTACTS_LAYOUT.DETAILS.TRIPS.EMPTY') }}
    </p>

    <div v-else class="flex flex-col mt-3 divide-y divide-n-weak">
      <!-- A row opens the trip. Six rows of facts an agent cannot act on is a report, not a
           record: the itinerary, the shortlists sent and the history all live behind this.
           The content source is deliberately not here - GDS versus NDC changes how a
           booking is serviced after sale, which is a detail-view concern, not a scanning
           one. -->
      <button
        v-for="trip in visible"
        :key="trip.id"
        type="button"
        class="flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5 text-sm text-left rounded hover:bg-n-alpha-1"
        @click="openTripId = trip.id"
      >
        <span class="font-medium text-n-slate-12">{{ route(trip) }}</span>
        <span v-if="dates(trip)" class="text-xs text-n-slate-11">{{
          dates(trip)
        }}</span>
        <span
          v-if="trip.record_locator"
          class="font-mono text-xs text-n-slate-11"
        >
          {{ trip.record_locator }}
        </span>
        <span
          class="px-2 py-0.5 ml-auto text-xs font-medium rounded-full bg-n-alpha-2 text-n-slate-11"
        >
          {{ t(`TRAVEL.TRIP_PANEL.STATUS.${trip.status.toUpperCase()}`) }}
        </span>
        <p
          v-if="deadline(trip)"
          class="w-full text-xs"
          :class="deadline(trip).urgent ? 'text-n-amber-11' : 'text-n-slate-10'"
        >
          {{ deadline(trip).text }}
        </p>
      </button>
    </div>

    <TripDetailsDialog
      v-if="openTripId"
      :key="openTripId"
      :trip-id="openTripId"
      @updated="replaceTrip"
      @close="openTripId = null"
    />
  </CardLayout>
</template>
